import { Button, Col, Form, FormGroup, Input, InputGroup, InputGroupText, Row } from "reactstrap";
import React from "react";
import withNavigateHook from "../../hooks/withNavigationHook";
import { authenticateUser } from "../../components/helpers/AuthenticationHelpers";
import { getSongBookPreferences } from "../../components/helpers/UserPreferenceHelpers";
import {AuthContext} from "../../components/helpers/AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Import eye icons from react-icons
import { debugLog } from "../../utils/debug";

class LoginForm extends React.Component {
    static contextType = AuthContext; // Enable context access within the class

    constructor(props) {
        super(props);
        this.state = {
            formSubmitted: false,
            email: "",
            password: "",
            emailError: "",
            passwordError: "",
            generalError: "",
            showPassword: false,         // Toggle flag for password visibility
        };
    }

    componentDidMount() {
        // Any initialization logic can stay here
    }

    onChangeEmail = (e) => {
        this.setState({
            email: e.target.value,
            emailError: "",
        });
    };

    onChangePassword = (e) => {
        this.setState({
            password: e.target.value,
            passwordError: "",
        });
    };

    validateAndSubmitForm = async (e) => {
        e.preventDefault(); // Prevent default form submission
        this.setState({ formSubmitted: true });
        let formHasError = false;

        if (!this.state.email || this.state.email.trim().length === 0) {
            this.setState({ emailError: "* You must provide an email address" });
            formHasError = true;
        }
        if (!this.state.password || this.state.password.trim().length === 0) {
            this.setState({ passwordError: "* You must provide a password" });
            formHasError = true;
        }

        if (!formHasError) {
            try {
                const success = await authenticateUser(this.state.email, this.state.password);
                if (success) {
                    if (typeof window.gtag === 'function') {
                        window.gtag('event', 'login', {method: 'email'});
                    }

                    // Since authenticateUser already sets sessionStorage, retrieve the customer data
                    const customerData = JSON.parse(sessionStorage.getItem("customer"));

                    // Update AuthContext
                    const { login } = this.context;
                    if (login) {
                        debugLog("Updating AuthContext with:", customerData); // Debug
                        login(customerData);
                    } else {
                        console.error("AuthContext login function not available");
                    }

                    // Fetch preferences and navigate
                    const preferences = await getSongBookPreferences();
                    sessionStorage.setItem("preferred-song-books", JSON.stringify(preferences));
                    const params = new URLSearchParams(window.location.search);
                    const redirect = params.get("redirect");
                    if (redirect && redirect.startsWith("/")) {
                        this.props.navigation(redirect);
                        return;
                    }
                    this.props.navigation('/next-steps');
                } else {
                    if (typeof window.gtag === 'function') {
                        window.gtag('event', 'login_failure', {
                            method: 'email'
                        });
                    }

                    this.setState({
                        generalError: "Sign in failed, please check the details provided and retry!",
                        formSubmitted: false,
                    });
                }
            } catch (error) {
                console.error("Error during sign-in:", error);
                this.setState({
                    generalError: "An error occurred during sign-in. Please try again.",
                    formSubmitted: false,
                });
            }
        } else {
            this.setState({ formSubmitted: false });
        }
    };

    loginButtonAvailable = () => {
        return (
            this.state.email &&
            this.state.password &&
            !this.state.formSubmitted
        );
    };

    togglePasswordVisibility = () => {
        this.setState((prevState) => ({
            showPassword: !prevState.showPassword,
        }));
    };

    render() {
        return (
            <>
                <Form role="form" onSubmit={this.validateAndSubmitForm}>
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-email-83" />
                                    </InputGroupText>
                                    <Input
                                        placeholder="Email"
                                        type="email"
                                        maxLength="200"
                                        onChange={this.onChangeEmail}
                                        value={this.state.email}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">{this.state.emailError}</span>
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-lock-circle-open" />
                                    </InputGroupText>
                                    <Input
                                        placeholder="Password"
                                        type={this.state.showPassword ? "text" : "password"}
                                        autoComplete="off"
                                        onChange={this.onChangePassword}
                                        value={this.state.password}
                                    />
                                    {/* Toggle button for password visibility */}
                                    <InputGroupText
                                        onClick={this.togglePasswordVisibility}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {this.state.showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </InputGroupText>
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">{this.state.passwordError}</span>
                            </Col>
                        </Row>
                    </FormGroup>
                    <Row lg="12">
                        <Col lg="12">
                            <span className="text-danger">{this.state.generalError}</span>
                        </Col>
                    </Row>
                    <Row lg="12">
                        <Col lg="12">
                            <p className="description mt-3">
                                Forgot your password?<br />
                                <a href="/password/reset/request">Request a password reset email</a>
                            </p>
                        </Col>
                    </Row>
                    <div className="text-center">
                        <Button
                            id="loginButton"
                            className="mt-4"
                            color="primary"
                            type="submit"
                            disabled={!this.loginButtonAvailable()}
                        >
                            Sign-In
                        </Button>
                    </div>
                </Form>
            </>
        );
    }
}

export default withNavigateHook(LoginForm);
